import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getChecklists, getChecklistWithDetails, getPunchlistActivity } from '../api/api';
import { filterPunchlists, computeDashboardData } from '../utils/punch-list-utils';
import type { PunchlistFilters, PunchlistSummary, Punchlist, ActivityLogEntry } from '../types';

export const punchListKeys = {
  all: ['punch-lists'] as const,
  list: (projectId: string, filters?: PunchlistFilters, page?: number, pageSize?: number) =>
    [...punchListKeys.all, 'list', projectId, filters, page, pageSize] as const,
  detail: (id: string) => [...punchListKeys.all, 'detail', id] as const,
  dashboard: (projectId: string) => [...punchListKeys.all, 'dashboard', projectId] as const,
  totalCount: (projectId: string) => [...punchListKeys.all, 'total-count', projectId] as const,
};

function useProjectId(): string {
  const { projectId } = useParams<{ projectId: string }>();
  return projectId ?? '';
}

export function usePunchLists(filters: PunchlistFilters, page: number = 1, pageSize: number = 25) {
  const projectId = useProjectId();

  const isTemplate =
    filters.type === 'template' ? true : filters.type === 'instance' ? false : undefined;
  const orderingPrefix = filters.sortOrder === 'asc' ? '' : '-';
  const ordering = `${orderingPrefix}${filters.sortBy}`;

  return useQuery<{
    items: Punchlist[];
    filteredCount: number;
    totalCount: number;
    page: number;
    pageSize: number;
  }>({
    queryKey: punchListKeys.list(projectId, filters, page, pageSize),
    queryFn: async () => {
      const [listResponse, totalResponse] = await Promise.all([
        getChecklists(projectId, {
          isTemplate,
          search: filters.search || undefined,
          ordering,
          page,
          pageSize,
        }),
        getChecklists(projectId, {
          isTemplate,
          page: 1,
          pageSize: 1,
        }),
      ]);

      // Status filtering is currently client-derived from nested item completion.
      // When status filtering is active, fetch a larger filtered window then paginate client-side.
      if (filters.status !== 'all') {
        const statusWindow = await getChecklists(projectId, {
          isTemplate,
          search: filters.search || undefined,
          ordering,
          page: 1,
          pageSize: 500,
        });
        const statusFilteredItems = filterPunchlists(statusWindow.results, {
          ...filters,
          search: '',
          type: 'all',
        });
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        return {
          items: statusFilteredItems.slice(startIndex, endIndex),
          filteredCount: statusFilteredItems.length,
          totalCount: totalResponse.count,
          page,
          pageSize,
        };
      }

      return {
        items: listResponse.results,
        filteredCount: listResponse.count,
        totalCount: totalResponse.count,
        page,
        pageSize,
      };
    },
    enabled: !!projectId,
  });
}

export function usePunchListDetail(id: string, enabled = true) {
  const projectId = useProjectId();

  return useQuery<Punchlist>({
    queryKey: punchListKeys.detail(id),
    queryFn: () => getChecklistWithDetails(id, projectId),
    enabled: enabled && !!id && !!projectId,
  });
}

export function usePunchListDashboard() {
  const projectId = useProjectId();

  return useQuery<PunchlistSummary>({
    queryKey: punchListKeys.dashboard(projectId),
    queryFn: async () => {
      const punchlists = await getChecklists(projectId, {
        isTemplate: false,
        page: 1,
        pageSize: 500,
      });
      return computeDashboardData(punchlists.results);
    },
    enabled: !!projectId,
  });
}

export function usePunchListActivityLog(punchlistId: string) {
  const projectId = useProjectId();

  return useQuery<ActivityLogEntry[]>({
    queryKey: [...punchListKeys.detail(punchlistId), 'activity'] as const,
    queryFn: () => getPunchlistActivity(punchlistId, projectId),
    enabled: !!punchlistId && !!projectId,
  });
}
